import React from 'react';
import { Card, CardContent, Typography, Box, keyframes, Tooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';

// Animations for GOD TIER ALERT
const pulseRed = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
`;

const pulseGreen = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(74, 222, 128, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
`;

const MetricCard = ({ label, value, subtext, color, icon: Icon, isPulsing, strength, tooltip }) => (
    <Card
        sx={{
            flex: 1,
            minWidth: 0,
            height: '110px', // FIX HEIGHT: Uniform across all cards
            background: 'rgba(30, 41, 59, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            animation: isPulsing
                ? `${color.includes('f87171') ? pulseRed : pulseGreen} 2s infinite`
                : 'none',
            borderLeft: isPulsing ? `4px solid ${color}` : '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
                borderColor: 'rgba(255,255,255,0.2)'
            },
        }}
    >
        <CardContent sx={{ p: '12px !important', '&:last-child': { pb: '12px !important' } }}>
            <Tooltip title={tooltip} arrow placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, cursor: 'help', width: 'fit-content' }}>
                    {Icon && <Icon sx={{ mr: 0.75, color: color || 'text.secondary', fontSize: '1rem' }} />}
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontWeight: 700,
                            color: 'text.secondary',
                            fontSize: '0.65rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            borderBottom: '1px dotted rgba(255,255,255,0.3)' // Hint interaction
                        }}
                    >
                        {label}
                    </Typography>
                </Box>
            </Tooltip>

            <Typography
                variant="h5" // DOWNGRADED from h4: Making it less "big/wide"
                sx={{
                    fontWeight: 800,
                    color: color || 'text.primary',
                    textShadow: isPulsing ? `0 0 15px ${color}` : 'none',
                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2
                }}
            >
                {value}
            </Typography>

            <Box sx={{ minHeight: '32px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                {subtext && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: isPulsing ? color : 'text.secondary',
                            fontWeight: isPulsing ? 700 : 500,
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {subtext}
                    </Typography>
                )}

                {strength && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.disabled',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            opacity: 0.7,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {strength}
                    </Typography>
                )}
            </Box>
        </CardContent>
    </Card>
);

const MetricsRow = ({ spyPrice, keyLevels, ticker = 'SPY' }) => {
    if (!keyLevels) return null;

    const formatPrice = (val) => `$${val.toFixed(2)}`;
    const formatStrike = (val) => `$${val.toFixed(0)}`;
    const formatGEX = (val) => {
        const absVal = Math.abs(val);
        const billions = absVal / 1e9;
        const millions = absVal / 1e6;
        if (billions >= 1) return `$${billions.toFixed(1)}B`;
        return `$${millions.toFixed(0)}M`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const spot = spyPrice;

    // Distances
    const callWallDist = ((keyLevels.call_wall - spot) / spot) * 100;
    const callWallClose = Math.abs(callWallDist) < 0.5;

    const putWallDist = ((spot - keyLevels.put_wall) / spot) * 100;
    const putWallClose = Math.abs(putWallDist) < 0.5;

    let flipDistStr = "N/A";
    if (keyLevels.gamma_flip) {
        const flipDist = ((keyLevels.gamma_flip - spot) / spot) * 100;
        flipDistStr = `${flipDist > 0 ? '+' : ''}${flipDist.toFixed(2)}%`;
    }

    return (
        <Box sx={{ mb: 3, maxWidth: '1400px', mx: 'auto' }}> {/* MaxWidth to prevent "too wide" feel */}
            <Typography variant="overline" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600, textAlign: 'center', fontSize: '0.68rem' }}>
                LIVE DATA • {formatDate(keyLevels.zero_dte_date)} Expiration (Next)
            </Typography>

            <Box sx={{
                display: 'flex',
                gap: 1.5,
                width: '100%',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                justifyContent: 'center'
            }}>

                <Box sx={{ flex: 1, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
                    <MetricCard
                        label={`${ticker} Price`}
                        value={formatPrice(spyPrice)}
                        subtext="Live Spot"
                        color="#fff"
                        icon={SpeedIcon}
                        tooltip={`Real-time ${ticker} spot price.`}
                    />
                </Box>

                <Box sx={{ flex: 1, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
                    <MetricCard
                        label="Net GEX (0DTE)"
                        value={formatGEX(keyLevels.total_gex)}
                        subtext={keyLevels.total_gex > 0 ? "Bullish / Low Vol" : "Bearish / High Vol"}
                        color={keyLevels.total_gex >= 0 ? '#4ade80' : '#f87171'}
                        icon={AccessTimeIcon}
                        tooltip="Total Net Gamma Exposure. Positive = dealers suppress volatility (mean reversion). Negative = dealers expand volatility (trend acceleration)."
                    />
                </Box>

                <Box sx={{ flex: 1, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
                    <MetricCard
                        label="Call Wall"
                        value={formatStrike(keyLevels.call_wall)}
                        subtext={`+${callWallDist.toFixed(2)}% away`}
                        strength={`Power: ${formatGEX(keyLevels.call_wall_gex)}`}
                        color="#4ade80"
                        icon={TrendingUpIcon}
                        isPulsing={callWallClose}
                        tooltip="Strike with the largest Net Positive Gamma. Often acts as a resistance level."
                    />
                </Box>

                <Box sx={{ flex: 1, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
                    <MetricCard
                        label="Put Wall"
                        value={formatStrike(keyLevels.put_wall)}
                        subtext={`-${putWallDist.toFixed(2)}% away`}
                        strength={`Power: ${formatGEX(keyLevels.put_wall_gex)}`}
                        color="#f87171"
                        icon={TrendingDownIcon}
                        isPulsing={putWallClose}
                        tooltip="Strike with the largest Net Negative Gamma. Often acts as a support magnet."
                    />
                </Box>

                <Box sx={{ flex: 1, minWidth: { xs: 'calc(50% - 8px)', md: 0 } }}>
                    <MetricCard
                        label="Gamma Flip"
                        value={keyLevels.gamma_flip ? formatStrike(keyLevels.gamma_flip) : 'N/A'}
                        subtext={`Dist: ${flipDistStr}`}
                        color="#a78bfa"
                        icon={SwapVertIcon}
                        tooltip="Strike price where Net Gamma switches polarity. A key pivot separating Mean Reversion (Low Vol) from Momentum (High Vol) regimes."
                    />
                </Box>

            </Box>
        </Box>
    );
};

export default MetricsRow;
